import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

const EMPTY = {
  rut: "", razon_social: "", giro: "", direccion: "",
  comuna: "", ciudad: "", email: "", telefono: "", contacto: "",
};

function ContactoForm({ form, setForm, onSubmit, submitLabel, testidPrefix }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid={`form-${testidPrefix}`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">RUT</label>
          <input required data-testid={`${testidPrefix}-rut`} value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })}
            placeholder="76.123.456-7" className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
        </div>
        <div>
          <label className="label-caps block mb-1">Razón social</label>
          <input required data-testid={`${testidPrefix}-razon`} value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
        </div>
      </div>
      <div>
        <label className="label-caps block mb-1">Giro</label>
        <input value={form.giro} onChange={(e) => setForm({ ...form, giro: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1">Teléfono</label>
          <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
        </div>
      </div>
      <div>
        <label className="label-caps block mb-1">Dirección</label>
        <input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Comuna</label>
          <input value={form.comuna} onChange={(e) => setForm({ ...form, comuna: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1">Ciudad</label>
          <input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
        </div>
      </div>
      <div>
        <label className="label-caps block mb-1">Persona de contacto</label>
        <input value={form.contacto} onChange={(e) => setForm({ ...form, contacto: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
      </div>
      <DialogFooter>
        <button type="submit" data-testid={`submit-${testidPrefix}`}
          className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">
          {submitLabel}
        </button>
      </DialogFooter>
    </form>
  );
}

function ContactosPage({ tipo, titulo, endpoint, testidPrefix }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null); // contact object or null
  const [form, setForm] = useState(EMPTY);
  const [editForm, setEditForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setList(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint]);

  const { pageItems, pagination, pageSize, setPage, setPageSize } = useClientPagination(list, endpoint);

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, form);
      toast.success(`${titulo.slice(0, -1)} creado`);
      setOpenNew(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`${endpoint}/${editing.id}`, editForm);
      toast.success("Cambios guardados");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({
      rut: c.rut || "",
      razon_social: c.razon_social || "",
      giro: c.giro || "",
      direccion: c.direccion || "",
      comuna: c.comuna || "",
      ciudad: c.ciudad || "",
      email: c.email || "",
      telefono: c.telefono || "",
      contacto: c.contacto || "",
    });
  };

  const del = async (id, name) => {
    if (!window.confirm(`¿Eliminar ${name}?`)) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <Page>
      <PageHeader
        title={titulo}
        subtitle={`Gestión de ${titulo.toLowerCase()}`}
        testid={`${testidPrefix}-header`}
        actions={
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <button data-testid={`new-${testidPrefix}-button`}
                className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm">
                <Plus className="h-4 w-4" /> Nuevo
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuevo {tipo}</DialogTitle>
                <DialogDescription>Crea un nuevo {tipo} con su RUT y datos de contacto.</DialogDescription>
              </DialogHeader>
              <ContactoForm form={form} setForm={setForm} onSubmit={submitNew} submitLabel="Crear" testidPrefix={`new-${testidPrefix}`} />
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No hay registros</div>
        ) : (
          <>
            <table className="acc-table" data-testid={`${testidPrefix}-table`}>
              <thead>
                <tr>
                  <th style={{ width: 140 }}>RUT</th>
                  <th>Razón social</th>
                  <th>Giro</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Ciudad</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((c) => (
                  <tr key={c.id} data-testid={`${testidPrefix}-row-${c.id}`}>
                    <td className="font-mono text-[12px]">{c.rut}</td>
                    <td className="font-medium">{c.razon_social}</td>
                    <td>{c.giro || "—"}</td>
                    <td>{c.email || "—"}</td>
                    <td>{c.telefono || "—"}</td>
                    <td>{c.ciudad || "—"}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(c)} data-testid={`edit-${testidPrefix}-${c.id}`}
                          title="Editar" className="text-primary hover:bg-primary/10 p-1 rounded-sm">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => del(c.id, c.razon_social)} data-testid={`delete-${testidPrefix}-${c.id}`}
                          title="Eliminar" className="text-destructive hover:bg-destructive/10 p-1 rounded-sm">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination
              pagination={pagination}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              onPageChange={setPage}
              testidPrefix={testidPrefix}
            />
          </>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar {tipo}</DialogTitle>
            <DialogDescription>Modifica los datos del {tipo}.</DialogDescription>
          </DialogHeader>
          {editing && (
            <ContactoForm form={editForm} setForm={setEditForm} onSubmit={submitEdit} submitLabel="Guardar cambios" testidPrefix={`edit-${testidPrefix}`} />
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}

export function Clientes() {
  return <ContactosPage tipo="cliente" titulo="Clientes" endpoint="/clientes" testidPrefix="cliente" />;
}

export function Proveedores() {
  return <ContactosPage tipo="proveedor" titulo="Proveedores" endpoint="/proveedores" testidPrefix="proveedor" />;
}
